import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useAuth } from '../services/AuthContext';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const { refreshAuth } = useAuth();
  const hasProcessedCallback = useRef(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Prevent double execution in React StrictMode
      if (hasProcessedCallback.current || processing) {
        return;
      }
      
      setProcessing(true);

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      if (code) {
        hasProcessedCallback.current = true; // Mark as processed
        
        try {
          // Set up a timeout for the request
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
          
          const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google/callback`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code }),
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Backend error:', errorText);
            throw new Error(`Authentication failed: ${errorText}`);
          }

          const data = await response.json();

          if (data.access_token && data.user) {
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('user', JSON.stringify(data.user));

            refreshAuth();
            window.location.href = '/dashboard';
          } else {
            throw new Error(`OAuth response missing required data: ${JSON.stringify(data)}`);
          }
          
        } catch (error) {
          console.error('OAuth callback failed:', error);
          console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name
          });
          hasProcessedCallback.current = false; // Reset on error
          setProcessing(false);
          
          // Show more specific error message
          let errorMessage = 'Authentication failed';
          if (error.message.includes('Access restricted to @nkpmedical.com')) {
            errorMessage = 'Access denied: Only @nkpmedical.com email addresses are allowed to sign in.';
          } else if (error.message.includes('Failed to exchange code')) {
            errorMessage = 'Google authentication failed. Please try again.';
          } else if (error.message.includes('fetch')) {
            errorMessage = 'Network error. Please check your connection and try again.';
          } else {
            errorMessage = error.message;
          }
          
          window.location.href = '/login?error=' + encodeURIComponent(errorMessage);
        }
      } else {
        // No code means this is not an OAuth callback, redirect to login
        window.location.href = '/login';
      }
    };

    handleOAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        gap: 2
      }}
    >
      <CircularProgress size={60} />
      <Typography variant="h6" color="text.secondary">
        Completing sign in...
      </Typography>
    </Box>
  );
};

export default OAuthCallback;